import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import fetchWithTenant from '../utils/fetchWithTenant';
import MdRouterDetail from './MdRouterDetail';
import CredentialPicker from '../components/CredentialPicker';
import '../styles/v2/pages/md-switch-detail.css';
import {
  ArrowLeft, RefreshCw, Activity, CheckCircle, AlertCircle, Loader, ExternalLink,
  Router as RouterIcon, Wifi, Shield, Network, Cable, Clock,
} from 'lucide-react';
import DeviceHeader from '../components/DeviceHeader';
import ControlPill from '../components/ControlPill';
import DeviceCard from '../components/DeviceCard';
import DeviceListRow from '../components/DeviceListRow';
import '../styles/v2/components/device-detail.css';

const CATEGORY_ICON = { ROUTER: RouterIcon, ACCESS_POINT: Wifi, FIREWALL: Shield, SWITCH: Network };

const POLL_INTERVAL = 15000;

// ─── Device type config (matches normalized model) ───────────────────────────
const DEVICE_TYPE = {
  camera:         { icon: '📹', color: '#06B6D4', label: 'IP Camera',      ccmPath: '/live-view',      ccmLabel: 'View in Live View' },
  access_control: { icon: '🚪', color: '#8B5CF6', label: 'Access Control', ccmPath: '/access-control', ccmLabel: 'View in Access Control' },
  intercom:       { icon: '🔔', color: '#F59E0B', label: 'Intercom',       ccmPath: '/intercoms',      ccmLabel: 'View in Intercoms' },
  access_point:   { icon: '📶', color: '#10B981', label: 'Access Point',   ccmPath: null,              ccmLabel: null },
  server:         { icon: '🖥️', color: '#64748B', label: 'NVR/Server',    ccmPath: null,              ccmLabel: null },
  router:         { icon: '🌐', color: '#3B82F6', label: 'Router',         ccmPath: null,              ccmLabel: null },
  switch:         { icon: '🔀', color: '#60A5FA', label: 'Switch',         ccmPath: null,              ccmLabel: null },
  nvr:            { icon: '🖥️', color: '#64748B', label: 'NVR',            ccmPath: null,              ccmLabel: null },
  access_controller: { icon: '🚪', color: '#8B5CF6', label: 'Access Control', ccmPath: '/access-control', ccmLabel: 'View in Access Control' },
  ups:            { icon: '🔋', color: '#F97316', label: 'UPS/PDU',         ccmPath: null,              ccmLabel: null },
  pdu:            { icon: '🔌', color: '#F97316', label: 'PDU',             ccmPath: null,              ccmLabel: null },
  unknown:        { icon: '❓', color: '#6B7280', label: 'Unknown',        ccmPath: null,              ccmLabel: null },
};

// Colors are theme tokens (theme-tokens.css), not hardcoded hex -- 'down'
// and 'disabled' have no dedicated status token (only ok/warn/error exist),
// so both reuse --text-tertiary, the same neutral DeviceHeader already
// uses for "offline" (not a fault state on its own). 'disabled' dims it
// further via color-mix so the two remain visually distinct.
const PORT_STATUS = {
  up:       { color: 'var(--status-ok)', bg: 'color-mix(in srgb, var(--status-ok) 15%, transparent)', label: 'Link Up',  glow: '0 0 8px color-mix(in srgb, var(--status-ok) 33%, transparent)' },
  down:     { color: 'var(--text-tertiary)', bg: 'color-mix(in srgb, var(--text-tertiary) 12%, transparent)', label: 'No Link',  glow: 'none' },
  error:    { color: 'var(--status-error)', bg: 'color-mix(in srgb, var(--status-error) 15%, transparent)', label: 'Errors',   glow: '0 0 8px color-mix(in srgb, var(--status-error) 33%, transparent)' },
  disabled: { color: 'color-mix(in srgb, var(--text-tertiary) 55%, transparent)', bg: 'color-mix(in srgb, var(--text-tertiary) 12%, transparent)', label: 'Disabled', glow: 'none' },
};

// ─── Cisco ifDescr parser (Phase 5) ──────────────────────────────────────────
function parseCiscoPort(name) {
  if (!name) return null;
  if (/^(Vlan|Loopback|Port-channel|Null|Tunnel|Liin|ucse|AppGig|BDI|nve)/i.test(name)) return null;
  if (/^(mgmt|Management)\d/i.test(name))
    return { type: 'mgmt', num: 0, speed: '1G', isSfp: false, isFast: false, isGig: false };
  const m = name.match(
    /^(FastEthernet|GigabitEthernet|TenGigabitEthernet|TwentyFiveGigE|FortyGigabitEthernet|HundredGigE)(\d+)\/(\d+)\/(\d+)/i
  );
  if (!m) return null;
  const prefix = m[1].toLowerCase();
  const portNum = parseInt(m[4], 10);
  let speed = '1G', isSfp = false;
  if (prefix.startsWith('fast'))          { speed = '100M'; }
  else if (prefix.startsWith('tengig'))   { speed = '10G';  isSfp = true; }
  else if (prefix.startsWith('twenty'))  { speed = '25G';  isSfp = true; }
  else if (prefix.startsWith('forty'))   { speed = '40G';  isSfp = true; }
  else if (prefix.startsWith('hundred')) { speed = '100G'; isSfp = true; }
  return { type: isSfp ? 'sfp' : 'access', num: portNum, speed, isSfp,
    isFast: prefix.startsWith('fast'), isGig: prefix === 'gigabitethernet' };
}
function promoteCiscoGigUplinks(parsedPorts) {
  const hasFast = parsedPorts.some(p => p.isFast);
  if (!hasFast) return;
  for (const p of parsedPorts) {
    if (p.isGig) { p.isSfp = true; p.type = 'sfp'; }
  }
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function timeAgo(d) {
  if (!d) return 'Never';
  const s = Math.floor((Date.now() - new Date(d)) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}
function formatUptime(sec) {
  if (!sec && sec !== 0) return '—';
  const d = Math.floor(sec / 86400), h = Math.floor((sec % 86400) / 3600), m = Math.floor((sec % 3600) / 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}
function formatBps(bps) {
  if (bps == null) return '—';
  if (bps === 0) return '0 bps';
  if (bps >= 1e9) return `${(bps / 1e9).toFixed(1)} Gbps`;
  if (bps >= 1e6) return `${(bps / 1e6).toFixed(1)} Mbps`;
  if (bps >= 1e3) return `${(bps / 1e3).toFixed(1)} Kbps`;
  return `${bps} bps`;
}
function formatPackets(n) {
  if (!n) return '0';
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return `${n}`;
}
function formatSpeed(mbps) {
  if (!mbps) return '—';
  if (mbps >= 1000) return `${mbps / 1000}G`;
  return `${mbps}M`;
}
function getPortStatus(port) {
  if (!port) return 'down';
  const os = port.linkStatus || port.operStatus;
  if (os === 'up' || os === 'connected') return 'up';
  if ((port.txErrors ?? 0) > 5 || (port.rxErrors ?? 0) > 5) return 'error';
  if (port.adminStatus === 'down' || port.adminStatus === 'disabled') return 'disabled';
  // Known simplification (868m48j4e): 'unknown' (a CONTROLLER-mode port
  // Omada has never seen real evidence for -- e.g. an assumed/idle port
  // filled in from the model's known port count, see omada.portProfiles.cjs)
  // falls through to the same 'down' bucket as a genuinely confirmed-down
  // port. An assumed-idle port and a real confirmed-down port render
  // identically ("No Link") as a result. Left as-is deliberately -- idle
  // probably does mean down in practice, and giving 'unknown' its own
  // visual state is a real UI change, not a one-line fix, so it's noted
  // here rather than folded into a linkStatus data-accuracy ticket.
  return 'down';
}

// ─── Mini Sparkline ───────────────────────────────────────────────────────────
function Sparkline({ data, color, width = 90, height = 28 }) {
  if (!data || data.length < 2) {
    return <div style={{ width, height, background: 'transparent' }} />;
  }
  const max = Math.max(...data, 1);
  const pts = data.map((v, i) =>
    `${(i / (data.length - 1)) * width},${height - (v / max) * (height - 2) - 1}`
  ).join(' ');
  const gradId = `sg-${color.replace('#', '')}`;
  return (
    <svg width={width} height={height} style={{ display: 'block', overflow: 'visible' }}>
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0.02" />
        </linearGradient>
      </defs>
      <polyline points={`0,${height} ${pts} ${width},${height}`} fill={`url(#${gradId})`} stroke="none" />
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  );
}

// ─── Port Button (physical panel) ─────────────────────────────────────────────
function PortButton({ port, isSelected, onClick }) {
  const status = getPortStatus(port);
  const sc = PORT_STATUS[status] || PORT_STATUS.down;
  const dtc = port.connectedDevice ? (DEVICE_TYPE[port.connectedDevice.type] || DEVICE_TYPE.unknown) : null;

  return (
    <button
      onClick={onClick}
      title={port.connectedDevice
        ? `${port.connectedDevice.name || port.connectedDevice.mac} (${port.connectedDevice.ip || '—'})`
        : `Port ${port.portNumber ?? port.label} — ${getPortStatus(port) === 'up' ? 'Connected (unknown device)' : 'No link'}`}
      style={{
        width: '100%', height: port.isSfp ? 42 : 34,
        borderRadius: port.isSfp ? 6 : 4,
        border: `1.5px solid ${isSelected ? '#00D4AA' : `color-mix(in srgb, ${sc.color} 33%, transparent)`}`,
        background: isSelected ? '#00D4AA18' : sc.bg,
        cursor: 'pointer', position: 'relative',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2,
        transition: 'all 0.15s',
        boxShadow: isSelected ? '0 0 12px #00D4AA33' : sc.glow,
        transform: isSelected ? 'scale(1.1)' : 'scale(1)',
        
      }}
      onMouseEnter={e => { if (!isSelected) { e.currentTarget.style.borderColor = `color-mix(in srgb, ${sc.color} 60%, transparent)`; e.currentTarget.style.transform = 'scale(1.06)'; e.currentTarget.style.boxShadow = `0 0 8px color-mix(in srgb, ${sc.color} 20%, transparent)`; } }}
      onMouseLeave={e => { if (!isSelected) { e.currentTarget.style.borderColor = `color-mix(in srgb, ${sc.color} 33%, transparent)`; e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = sc.glow; } }}
    >
      <div style={{ width: 5, height: 5, borderRadius: '50%', background: sc.color, boxShadow: (status === 'up' || status === 'error') ? `0 0 5px ${sc.color}` : 'none' }} />
      <span style={{ fontSize: 11, fontFamily: 'monospace', fontWeight: isSelected ? 700 : 500, color: isSelected ? '#00D4AA' : '#94A3B8', letterSpacing: '-0.02em' }}>
        {port.isSfp ? port.label : (port.portNumber ?? port.label)}
      </span>
      {(port.poeEnabled && (port.poeWatts ?? 0) > 0) && (
        <div style={{ position: 'absolute', top: -3, right: -3, width: 7, height: 7, borderRadius: '50%', background: '#F59E0B', border: '1.5px solid #0A0F1A' }} title="PoE Active" />
      )}
      {dtc && status === 'up' && (
        <div style={{ position: 'absolute', bottom: 0, left: 2, right: 2, height: 2, borderRadius: '0 0 3px 3px', background: dtc.color, opacity: 0.7 }} />
      )}
    </button>
  );
}

// ─── Port Detail Panel ────────────────────────────────────────────────────────
function PortDetailPanel({ port, sparkData, onClose, sw, navigate }) {
  const status = getPortStatus(port);
  const sc = PORT_STATUS[status] || PORT_STATUS.down;
  const dev = port.connectedDevice;
  const dtc = dev ? (DEVICE_TYPE[dev.type] || DEVICE_TYPE.unknown) : null;
  const portKey = port.id || port.portNumber;
  const spark = sparkData?.[portKey] || {};

  return (
    <div style={{ display: 'grid', gridTemplateColumns: dev ? '1fr 1fr' : '1fr', gap: 16, marginBottom: 20, animation: 'fadeIn 0.2s ease' }}>

      {/* ── Port Stats ── */}
      <DeviceCard style={{ padding: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: `color-mix(in srgb, ${sc.color} 12%, transparent)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ width: 10, height: 10, borderRadius: '50%', background: sc.color, boxShadow: `0 0 8px color-mix(in srgb, ${sc.color} 40%, transparent)` }} />
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700 }}>Port {port.isSfp ? port.label : (port.portNumber ?? port.label)}</div>
              <div style={{ fontSize: 11, color: sc.color }}>{sc.label} · {port.speedFormatted || formatSpeed(port.speed)} · {port.duplex || '—'}</div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-tertiary)', cursor: 'pointer', fontSize: 18, padding: 4 }}>✕</button>
        </div>

        {/* Traffic */}
        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-tertiary)', marginBottom: 8, letterSpacing: '0.08em' }}>TRAFFIC</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
          {[
            { label: '↑ TX', bps: port.txBps, packets: port.txPackets, color: '#3B82F6', sp: spark.tx },
            { label: '↓ RX', bps: port.rxBps, packets: port.rxPackets, color: '#10B981', sp: spark.rx },
          ].map(({ label, bps, packets, color, sp }) => (
            <div key={label} className="stat-widget stat-widget-compact">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{label}</span>
                <span style={{ fontSize: 14, fontWeight: 700, color }}>{formatBps(bps)}</span>
              </div>
              <Sparkline data={sp} color={color} width={90} height={28} />
              <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginTop: 3 }}>{formatPackets(packets)} pkts</div>
            </div>
          ))}
        </div>

        {/* Error counters */}
        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-tertiary)', marginBottom: 8, letterSpacing: '0.08em' }}>COUNTERS</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 16 }}>
          {[
            { label: 'TX Errors', value: port.txErrors ?? 0, warn: (port.txErrors ?? 0) > 5 },
            { label: 'RX Errors', value: port.rxErrors ?? 0, warn: (port.rxErrors ?? 0) > 5 },
            { label: 'Collisions', value: port.collisions ?? 0, warn: (port.collisions ?? 0) > 0 },
          ].map((c, i) => (
            <div key={i} className="stat-widget stat-widget-compact" style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: c.warn ? '#EF4444' : '#10B981' }}>{c.value}</div>
              <div style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>{c.label}</div>
            </div>
          ))}
        </div>

        {/* PoE */}
        {port.poeEnabled && (port.poeWatts > 0 || port.poeMaxWatts > 0) && (
          <>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-tertiary)', marginBottom: 8, letterSpacing: '0.08em' }}>PoE POWER</div>
            <div className="stat-widget stat-widget-compact">
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{port.poeClass || 'PoE'}</span>
                <span style={{ fontSize: 14, fontWeight: 700, color: '#F59E0B' }}>
                  {(port.poeWatts || 0).toFixed(1)}W
                  {port.poeMaxWatts > 0 && <span style={{ fontSize: 11, color: 'var(--text-tertiary)', fontWeight: 400 }}> / {port.poeMaxWatts}W</span>}
                </span>
              </div>
              {port.poeMaxWatts > 0 && (
                <div style={{ height: 6, borderRadius: 3, background: '#1A2235', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${Math.min(((port.poeWatts || 0) / port.poeMaxWatts) * 100, 100)}%`, borderRadius: 3, background: 'linear-gradient(90deg, #F59E0B, #FB923C)' }} />
                </div>
              )}
            </div>
          </>
        )}

        {/* Port metadata */}
        <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
          {[
            { k: 'VLAN', v: port.vlanId || port.vlan || '—' },
            { k: 'Port Type', v: port.portType || port.portMode || '—' },
            { k: 'MTU', v: port.mtu || '—' },
            { k: 'Admin', v: port.adminStatus || 'enabled' },
          ].map((r, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid #1E293B' }}>
              <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{r.k}</span>
              <span style={{ fontSize: 11, color: '#CBD5E1', fontFamily: 'monospace', textTransform: 'capitalize' }}>{r.v}</span>
            </div>
          ))}
        </div>
      </DeviceCard>

      {/* ── Connected Device ── */}
      {dev && dtc ? (
        <DeviceCard style={{ borderColor: `${dtc.color}33`, padding: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <div style={{ width: 44, height: 44, borderRadius: 10, background: `${dtc.color}15`, border: `1px solid ${dtc.color}33`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, flexShrink: 0 }}>{dtc.icon}</div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700 }}>{dev.name || 'Unknown Device'}</div>
              <div style={{ display: 'flex', gap: 6, marginTop: 3, flexWrap: 'wrap' }}>
                <span style={{ padding: '1px 8px', borderRadius: 4, background: `${dtc.color}20`, color: dtc.color, fontSize: 10, fontWeight: 600 }}>{dtc.label}</span>
                <span style={{ padding: '1px 8px', borderRadius: 4, background: '#10B98120', color: '#10B981', fontSize: 10, fontWeight: 600 }}>Connected</span>
              </div>
            </div>
          </div>

          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-tertiary)', marginBottom: 8, letterSpacing: '0.08em' }}>DEVICE INFO</div>
          <DeviceListRow style={{ padding: 12, marginBottom: 14 }}>
            {[
              { k: 'IP Address', v: dev.ip || dev.resolvedIp || '—' },
              { k: 'MAC Address', v: dev.mac || dev.macAddress || '—' },
              { k: 'VLAN', v: dev.vlan?.toString() || dev.learnedVlan?.toString() || '—' },
              { k: 'Manufacturer', v: dev.manufacturer || '—' },
              ...(dev.resolvedHostname ? [{ k: 'Hostname', v: dev.resolvedHostname }] : []),
            ].map((r, i, arr) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: i < arr.length - 1 ? '1px solid #1A2235' : 'none' }}>
                <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{r.k}</span>
                <span style={{ fontSize: 12, color: 'var(--text-primary)', fontFamily: 'monospace' }}>{r.v}</span>
              </div>
            ))}
          </DeviceListRow>

          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-tertiary)', marginBottom: 8, letterSpacing: '0.08em' }}>BANDWIDTH USAGE</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 14 }}>
            <DeviceListRow style={{ padding: '10px 12px', textAlign: 'center' }}>
              <div style={{ fontSize: 11, color: '#3B82F6', marginBottom: 2 }}>Upload</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>{formatBps(port.txBps)}</div>
            </DeviceListRow>
            <DeviceListRow style={{ padding: '10px 12px', textAlign: 'center' }}>
              <div style={{ fontSize: 11, color: '#10B981', marginBottom: 2 }}>Download</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>{formatBps(port.rxBps)}</div>
            </DeviceListRow>
          </div>

          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-tertiary)', marginBottom: 8, letterSpacing: '0.08em' }}>NETWORK PATH</div>
          <DeviceListRow style={{ padding: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <span style={{ padding: '4px 10px', borderRadius: 6, background: '#3B82F615', border: '1px solid #3B82F633', fontSize: 11, color: '#60A5FA' }}>🌐 Uplink</span>
              <span style={{ color: '#334155', fontSize: 12 }}>→</span>
              <span style={{ padding: '4px 10px', borderRadius: 6, background: '#00D4AA15', border: '1px solid #00D4AA33', fontSize: 11, color: '#00D4AA' }}>🔌 Port {port.isSfp ? port.label : (port.portNumber ?? port.label)}</span>
              <span style={{ color: '#334155', fontSize: 12 }}>→</span>
              <span style={{ padding: '4px 10px', borderRadius: 6, background: `${dtc.color}15`, border: `1px solid ${dtc.color}33`, fontSize: 11, color: dtc.color }}>{dtc.icon} {dev.name || 'Device'}</span>
            </div>
            <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginTop: 8 }}>
              VLAN {dev.vlan || dev.learnedVlan || '—'} · {port.speedFormatted || formatSpeed(port.speed)} · {port.duplex || 'Full'}
            </div>
          </DeviceListRow>

          {dev && dev.ip && sw?.agentId && (
            <div style={{ marginTop: 14, padding: '10px 14px', borderRadius: 8, background: 'linear-gradient(135deg, #00D4AA10, #06B6D410)', border: '1px solid #00D4AA22' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 14 }}>🔗</span>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#00D4AA' }}>Linked in ServerliCCM</div>
                    <div style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>{dtc.ccmLabel || dtc.label}</div>
                  </div>
                </div>
                <button onClick={() => navigate(`/monitoring/manage/${sw.agentId}/${dev.ip}/80?name=${encodeURIComponent(dev.name || dev.mac || dev.ip)}&return=/monitoring/network/${sw.id}`)} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 6, background: '#00D4AA20', color: '#00D4AA', fontSize: 11, fontWeight: 600, border: 'none', cursor: 'pointer' }}>
                  Manage Device <ExternalLink size={10} />
                </button>
              </div>
            </div>
          )}
        </DeviceCard>
      ) : (
        <DeviceCard style={{ padding: 20, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 140 }}>
          <div style={{ fontSize: 36, marginBottom: 10, opacity: 0.2 }}>🔌</div>
          <div style={{ fontSize: 14, color: 'var(--text-tertiary)', marginBottom: 4 }}>No Device Connected</div>
          <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>Port {port.portNumber ?? port.label} has no active link</div>
        </DeviceCard>
      )}
    </div>
  );
}

// ─── Physical Switch Panel ────────────────────────────────────────────────────
function SwitchPanel({ sw, ports, selectedPort, onSelectPort, chassisLabel }) {
  const ethPorts = ports.filter(p => !p.isSfp);
  const sfpPorts = ports.filter(p => p.isSfp);
  const topRow = ethPorts.filter((_, i) => i % 2 === 0);
  const bottomRow = ethPorts.filter((_, i) => i % 2 === 1);
  const portsUp = ports.filter(p => getPortStatus(p) === 'up').length;
  const portsErr = ports.filter(p => getPortStatus(p) === 'error').length;

  return (
    <DeviceCard className="switch-front-panel" style={{ padding: '18px 22px', marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', letterSpacing: '0.1em' }}>
          {chassisLabel ? `FRONT PANEL — ${chassisLabel} — ${sw.model || 'SWITCH'}` : `FRONT PANEL — ${sw.model || sw.category || 'SWITCH'}`}
        </div>
        <div style={{ display: 'flex', gap: 14, fontSize: 11 }}>
          {Object.entries(PORT_STATUS).map(([k, v]) => (
            <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <div style={{ width: 7, height: 7, borderRadius: '50%', background: v.color }} />
              <span style={{ color: 'var(--text-tertiary)' }}>{v.label}</span>
            </div>
          ))}
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#F59E0B' }} />
            <span style={{ color: 'var(--text-tertiary)' }}>PoE</span>
          </div>
        </div>
      </div>

      {/* Chassis */}
      {(() => { const topCols = topRow.length + (sfpPorts.length > 0 ? Math.ceil(sfpPorts.length / 2) + 1 : 0); const botCols = bottomRow.length + (sfpPorts.length > 1 ? sfpPorts.length - Math.ceil(sfpPorts.length / 2) + 1 : 0); const maxCols = Math.max(topCols, botCols); return (
      <div style={{ background: '#0A0F1A', borderRadius: 10, padding: '14px 18px', border: '1px solid #1A2235' }}>
        {/* Top row */}
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${maxCols}, 1fr)`, gap: 'var(--space-sm)', marginBottom: 4 }}>
          {topRow.map(p => {
            const key = p.id || p.portNumber;
            return <PortButton key={key} port={p} isSelected={selectedPort === key} onClick={() => onSelectPort(selectedPort === key ? null : key)} />;
          })}
          {sfpPorts.length > 0 && (
            <>
              <div style={{ width: '100%', height: '100%', background: '#1E293B22', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><div style={{ width: 1, height: '60%', background: '#1E293B' }} /></div>
              {sfpPorts.slice(0, Math.ceil(sfpPorts.length / 2)).map(p => {
                const key = p.id || p.label;
                return <PortButton key={key} port={p} isSelected={selectedPort === key} onClick={() => onSelectPort(selectedPort === key ? null : key)} />;
              })}
            </>
          )}
        </div>
        {/* Bottom row */}
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${maxCols}, 1fr)`, gap: 'var(--space-sm)' }}>
          {bottomRow.map(p => {
            const key = p.id || p.portNumber;
            return <PortButton key={key} port={p} isSelected={selectedPort === key} onClick={() => onSelectPort(selectedPort === key ? null : key)} />;
          })}
          {sfpPorts.length > 1 && (
            <>
              <div style={{ width: '100%', height: '100%', background: '#1E293B22', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><div style={{ width: 1, height: '60%', background: '#1E293B' }} /></div>
              {sfpPorts.slice(Math.ceil(sfpPorts.length / 2)).map(p => {
                const key = p.id || p.label;
                return <PortButton key={key} port={p} isSelected={selectedPort === key} onClick={() => onSelectPort(selectedPort === key ? null : key)} />;
              })}
            </>
          )}
        </div>
      </div>
      ); })()}

      <div style={{ display: 'flex', gap: 16, marginTop: 10, fontSize: 11, color: 'var(--text-tertiary)' }}>
        <span><span style={{ color: '#10B981', fontWeight: 600 }}>{portsUp}</span> up</span>
        {portsErr > 0 && <span><span style={{ color: '#EF4444', fontWeight: 600 }}>{portsErr}</span> errors</span>}
        <span><span style={{ color: 'var(--text-tertiary)', fontWeight: 600 }}>{ports.length - portsUp}</span> unused</span>
        <span style={{ color: '#374151' }}>·</span>
        <span>Click a port to inspect · ⚡ = PoE active</span>
      </div>
    </DeviceCard>
  );
}

// ─── Port Table ───────────────────────────────────────────────────────────────
function PortTable({ ports, selectedPort, onSelectPort, sw }) {
  if (!ports.length) return null;
  return (
    <DeviceCard className="scd-ports-table-card" style={{ overflow: 'hidden' }}>
      <div style={{ padding: '12px 18px', borderBottom: '1px solid #1E293B', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 14, fontWeight: 600 }}>All {sw?.category === 'ROUTER' ? 'Interfaces' : sw?.category === 'ACCESS_POINT' ? 'Links' : 'Ports'}</span>
        <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
          {ports.filter(p => getPortStatus(p) === 'up').length} active · {ports.filter(p => getPortStatus(p) === 'error').length} errors · {ports.length - ports.filter(p => getPortStatus(p) === 'up').length} unused
        </span>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: '#0F1629' }}>
              {['Port', 'Status', 'Speed', 'Connected Device', 'Type', 'IP Address', 'PoE', 'Errors'].map(h => (
                <th key={h} style={{ padding: '10px 12px', textAlign: 'left', color: 'var(--text-tertiary)', fontWeight: 600, fontSize: 11, borderBottom: '1px solid #1E293B', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ports.map((port, idx) => {
              const key = port.id || port.portNumber || idx;
              const status = getPortStatus(port);
              const sc = PORT_STATUS[status] || PORT_STATUS.down;
              const dev = port.connectedDevice;
              const dtc = dev ? (DEVICE_TYPE[dev.type] || DEVICE_TYPE.unknown) : null;
              const isSelected = selectedPort === key;
              const totalErr = (port.txErrors ?? 0) + (port.rxErrors ?? 0);
              return (
                <tr key={key} onClick={() => onSelectPort(isSelected ? null : key)}
                  style={{ cursor: 'pointer', background: isSelected ? '#00D4AA08' : 'transparent', borderBottom: '1px solid #1A2235', transition: 'background 0.15s' }}
                  onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = '#ffffff04'; }}
                  onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                >
                  <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontWeight: 600, color: isSelected ? '#00D4AA' : '#CBD5E1' }}>
                    {sw?.category === 'ROUTER' ? (port.label || port.portNumber || idx + 1) : port.isSfp ? port.label : (port.portNumber ?? idx + 1)}
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                      <div style={{ width: 6, height: 6, borderRadius: '50%', background: sc.color, boxShadow: status === 'up' ? `0 0 4px ${sc.color}` : 'none' }} />
                      <span style={{ color: sc.color, fontSize: 11 }}>{sc.label}</span>
                    </div>
                  </td>
                  <td style={{ padding: '8px 12px', color: 'var(--text-secondary)', fontFamily: 'monospace', fontSize: 11 }}>{port.speedFormatted || formatSpeed(port.speed)}</td>
                  <td style={{ padding: '8px 12px', color: 'var(--text-primary)', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{dev?.name || '—'}</td>
                  <td style={{ padding: '8px 12px' }}>
                    {dtc ? <span style={{ padding: '1px 8px', borderRadius: 4, background: `${dtc.color}15`, color: dtc.color, fontSize: 10, fontWeight: 600 }}>{dtc.icon} {dtc.label}</span> : <span style={{ color: 'var(--text-tertiary)' }}>—</span>}
                  </td>
                  <td style={{ padding: '8px 12px', color: 'var(--text-secondary)', fontFamily: 'monospace', fontSize: 11 }}>{dev?.ip || dev?.resolvedIp || '—'}</td>
                  <td style={{ padding: '8px 12px' }}>
                    {(port.poeWatts ?? 0) > 0 ? <span style={{ color: '#F59E0B', fontWeight: 600 }}>⚡ {port.poeWatts.toFixed(1)}W</span> : <span style={{ color: '#374151' }}>—</span>}
                  </td>

                  <td style={{ padding: '8px 12px' }}>
                    <span style={{ color: totalErr > 5 ? '#EF4444' : '#475569', fontWeight: totalErr > 5 ? 600 : 400 }}>{totalErr}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </DeviceCard>
  );
}

// ─── Port Cards (mobile alternative to PortTable) ─────────────────────────────
// Same ports array and the same selectedPort toggle as PortTable, so a card
// tap opens the identical PortDetailPanel. CSS shows the table on wide screens
// and these cards under 768px.
function PortCards({ ports, selectedPort, onSelectPort, sw }) {
  if (!ports.length) return null;
  return (
    <div className="scd-port-cards">
      {ports.map((port, idx) => {
        const key = port.id || port.portNumber || idx;
        const status = getPortStatus(port);
        const sc = PORT_STATUS[status] || PORT_STATUS.down;
        const dev = port.connectedDevice;
        const dtc = dev ? (DEVICE_TYPE[dev.type] || DEVICE_TYPE.unknown) : null;
        const isSelected = selectedPort === key;
        const totalErr = (port.txErrors ?? 0) + (port.rxErrors ?? 0);
        const poeActive = (port.poeWatts ?? 0) > 0;
        const portLabel = sw?.category === 'ROUTER'
          ? (port.label || port.portNumber || idx + 1)
          : port.isSfp ? port.label : (port.portNumber ?? idx + 1);
        return (
          <button
            key={key}
            type="button"
            onClick={() => onSelectPort(isSelected ? null : key)}
            className={`scd-port-card${isSelected ? ' scd-port-card-selected' : ''}`}
          >
            <div className="scd-port-card-head">
              <span className="scd-port-card-num">{portLabel}</span>
              <span className={`scd-port-status scd-status-${status}`}>
                <span className="scd-port-status-dot" />
                {sc.label}
              </span>
              <span className="scd-port-card-device">{dev?.name || '\u2014'}</span>
            </div>
            <div className="scd-port-card-grid">
              <div className="scd-port-field">
                <span className="scd-port-field-label">Speed</span>
                <span className="scd-port-field-val">{port.speedFormatted || formatSpeed(port.speed)}</span>
              </div>
              <div className="scd-port-field">
                <span className="scd-port-field-label">Type</span>
                <span className="scd-port-field-val">{dtc ? `${dtc.icon} ${dtc.label}` : '\u2014'}</span>
              </div>
              <div className="scd-port-field">
                <span className="scd-port-field-label">IP Address</span>
                <span className="scd-port-field-val">{dev?.ip || dev?.resolvedIp || '\u2014'}</span>
              </div>
              <div className="scd-port-field">
                <span className="scd-port-field-label">PoE</span>
                <span className={`scd-port-field-val${poeActive ? ' scd-poe-active' : ''}`}>
                  {poeActive ? `⚡ ${port.poeWatts.toFixed(1)}W` : '\u2014'}
                </span>
              </div>
              <div className="scd-port-field">
                <span className="scd-port-field-label">Errors</span>
                <span className={`scd-port-field-val${totalErr > 5 ? ' scd-errors-warn' : ''}`}>{totalErr}</span>
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

// ─── Environment Card (Cisco ENVMON) ──────────────────────────────────────────
function EnvironmentCard({ envSnapshot }) {
  const [expanded, setExpanded] = useState(false);
  if (!envSnapshot || envSnapshot.length === 0) return null;

  const temps = envSnapshot.filter(s => s.sensorType === 'temperature');
  const fans = envSnapshot.filter(s => s.sensorType === 'fan');
  const psus = envSnapshot.filter(s => s.sensorType === 'psu');
  const problems = envSnapshot.filter(s => s.state !== 'normal' && s.state !== 'notPresent');
  const allNormal = problems.length === 0;
  const isExpanded = expanded || !allNormal;

  const stateColor = (state) => {
    switch (state) {
      case 'normal': return '#10B981';
      case 'warning': return '#F59E0B';
      case 'critical': case 'shutdown': case 'notFunctioning': return '#EF4444';
      case 'notPresent': return '#374151';
      default: return '#6B7280';
    }
  };

  const summary = [
    temps.length > 0 && `${temps.length} temp`,
    fans.length > 0 && `${fans.length} fan${fans.length > 1 ? 's' : ''}`,
    psus.length > 0 && `${psus.length} PSU`,
  ].filter(Boolean).join(', ');

  const SensorRow = ({ sensor }) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '6px 12px', borderRadius: 6,
      background: sensor.state !== 'normal' && sensor.state !== 'notPresent'
        ? `${stateColor(sensor.state)}15` : 'transparent' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 13, opacity: 0.7 }}>
          {sensor.sensorType === 'temperature' ? '\u{1F321}' : sensor.sensorType === 'fan' ? '\u{1F300}' : '\u{26A1}'}
        </span>
        <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{sensor.name || sensor.sensorType}</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {sensor.sensorType === 'temperature' && sensor.value > 0 && (
          <span style={{ fontSize: 12, color: 'var(--text-primary)', fontWeight: 600 }}>{sensor.value}&deg;C</span>
        )}
        <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600,
          color: stateColor(sensor.state),
          background: `${stateColor(sensor.state)}20` }}>
          {sensor.state.toUpperCase()}
        </span>
      </div>
    </div>
  );

  return (
    <DeviceCard style={{ borderColor: allNormal ? undefined : `${stateColor(problems[0]?.state)}44`, padding: '12px 14px', marginBottom: 20 }}>
      <div onClick={() => setExpanded(!expanded)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', userSelect: 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%',
            background: allNormal ? '#10B981' : stateColor(problems[0]?.state),
            boxShadow: allNormal ? 'none' : `0 0 8px ${stateColor(problems[0]?.state)}88` }} />
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
            Environment{allNormal ? ': All normal' : `: ${problems.length} issue${problems.length > 1 ? 's' : ''}`}
          </span>
          <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{summary}</span>
        </div>
        <span style={{ color: 'var(--text-tertiary)', fontSize: 12, transition: 'transform 0.2s',
          transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>&#9662;</span>
      </div>
      {isExpanded && (
        <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {!allNormal && problems.map((s, i) => <SensorRow key={`p-${i}`} sensor={s} />)}
          {!allNormal && <div style={{ height: 1, background: '#1E293B', margin: '4px 0' }} />}
          {temps.filter(s => allNormal || s.state === 'normal' || s.state === 'notPresent').map((s, i) => <SensorRow key={`t-${i}`} sensor={s} />)}
          {fans.filter(s => allNormal || s.state === 'normal' || s.state === 'notPresent').map((s, i) => <SensorRow key={`f-${i}`} sensor={s} />)}
          {psus.filter(s => allNormal || s.state === 'normal' || s.state === 'notPresent').map((s, i) => <SensorRow key={`s-${i}`} sensor={s} />)}
        </div>
      )}
    </DeviceCard>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function MdSwitchDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [sw, setSw] = useState(null);
  const [ports, setPorts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [portsLoading, setPortsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [selectedPort, setSelectedPort] = useState(null);
  const [sparkData, setSparkData] = useState({});
  const [switchRates, setSwitchRates] = useState({ rates: [], aggregate: { txBps: 0, rxBps: 0 } });
  const [wirelessClients, setWirelessClients] = useState([]);
  const timerRef = useRef(null);

  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3500); };

  const fetchSwitch = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await fetchWithTenant(`/api/md/switches/${id}`, {});
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setSw(data.switch || data);
      setError(null);
    } catch (e) { setError(e.message); }
    finally { if (!silent) setLoading(false); }
  }, [id]);

  const fetchPorts = useCallback(async () => {
    setPortsLoading(true);
    try {
      const res = await fetchWithTenant(`/api/md/switches/${id}/ports`, {});
      if (!res.ok) return;
      const data = await res.json();
      let raw = data.ports || data || [];
      const isCisco = (sw?.manufacturer || '').toUpperCase() === 'CISCO';

      // Cisco: parse ifDescr, filter virtual interfaces, promote GigE uplinks on 2960-class
      if (isCisco) {
        raw = raw.map(p => ({ ...p, _cisco: parseCiscoPort(p.portName || p.name || '') }))
                 .filter(p => p._cisco !== null);
        promoteCiscoGigUplinks(raw.map(p => p._cisco));
        raw.sort((a, b) => {
          if (a._cisco.isSfp !== b._cisco.isSfp) return a._cisco.isSfp ? 1 : -1;
          return a._cisco.num - b._cisco.num;
        });
      }

      setPorts(raw.map((p, i) => ({
        ...p,
        id: p.id || p.portId || i + 1,
        portNumber: p._cisco ? p._cisco.num : (p.displayPort ?? p.portNumber ?? p.portIndex ?? i + 1),
        label: p._cisco ? String(p._cisco.num) : (p.name || p.portName || String(i + 1)),
        speedFormatted: p.speedFormatted || (p._cisco ? p._cisco.speed : null),
        isSfp: p._cisco ? p._cisco.isSfp : (p.portType === 'sfp' || p.portType === 'uplink' || (p.name || '').toUpperCase().startsWith('SFP') || /^Port t\d/i.test(p.portName || p.name || '')),
        linkStatus: (p.linkStatus || p.operStatus || (p.isConnected ? 'up' : 'down')).toLowerCase(),
        txBps: p.txBps ?? 0, rxBps: p.rxBps ?? 0,
        txPackets: p.txPackets ?? 0, rxPackets: p.rxPackets ?? 0,
        txErrors: p.txErrors ?? 0, rxErrors: p.rxErrors ?? 0, collisions: p.collisions ?? 0,
        poeEnabled: p.poeEnabled ?? false, poeWatts: p.poeWatts ?? 0, poeMaxWatts: p.poeMaxWatts ?? 0,
        connectedDevice: p.connectedDevice || (p.macAddress ? {
          name: p.resolvedHostname || p.macAddress,
          ip: p.resolvedIp || null, mac: p.macAddress,
          type: p.deviceType || 'unknown', vlan: p.learnedVlan,
        } : null),
      })));
    } catch { /* best-effort */ }
    finally { setPortsLoading(false); }
  }, [id]);

  useEffect(() => {
    fetchSwitch(); fetchPorts();
    timerRef.current = setInterval(() => { fetchSwitch(true); fetchPorts(); }, POLL_INTERVAL);
    return () => clearInterval(timerRef.current);
  }, [fetchSwitch, fetchPorts]);

  // Wireless Clients tab (868m26xrv): only meaningful for an ACCESS_POINT
  // row, fetched once the switch record (and its category) is known.
  useEffect(() => {
    if (sw?.category !== 'ACCESS_POINT') return;
    fetchWithTenant(`/api/md/switches/${id}/wireless-clients`, {})
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => setWirelessClients(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [id, sw?.category]);

  // Fetch per-port sparkline data when a port is selected
  useEffect(() => {
    if (selectedPort === null) return;
    const portData = ports.find(p => (p.id || p.portNumber) === selectedPort || p.label === selectedPort);
    const portId = portData?.portId || portData?.id || selectedPort;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchWithTenant(`/api/md/switches/${id}/metrics/rates?portId=${portId}&points=20&hours=1`);
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (cancelled) return;
        setSparkData(prev => ({
          ...prev,
          [selectedPort]: {
            tx: (data.rates || []).map(r => r.txBps || 0),
            rx: (data.rates || []).map(r => r.rxBps || 0),
          }
        }));
      } catch { /* best-effort */ }
    })();
    return () => { cancelled = true; };
  }, [selectedPort, id, ports]);

  // Fetch switch-level aggregate throughput for header sparkline
  useEffect(() => {
    let cancelled = false;
    const fetchRates = async () => {
      try {
        const res = await fetchWithTenant(`/api/md/switches/${id}/metrics/rates?points=20&hours=1`);
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (!cancelled) setSwitchRates(data);
      } catch { /* best-effort */ }
    };
    fetchRates();
    const interval = setInterval(fetchRates, 60000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [id]);

  const handleForcePoll = async () => {
    setActionLoading(true);
    try {
      const res = await fetchWithTenant(`/api/md/switches/${id}/poll`, { method: 'POST' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      // CONTROLLER-mode rows are synced from the controller, not polled by
      // the agent (switch.routes.cjs branches this same endpoint) -- a
      // controller sync runs synchronously, so the row is already fresh.
      showToast(sw?.pollMode === 'CONTROLLER' ? 'Synced from controller' : 'Force poll sent — refreshing in 5s');
      setTimeout(() => { fetchSwitch(true); fetchPorts(); }, sw?.pollMode === 'CONTROLLER' ? 0 : 5000);
    } catch (e) { showToast(`Poll failed: ${e.message}`, 'error'); }
    finally { setActionLoading(false); }
  };

  const handleStartMonitoring = async () => {
    setActionLoading(true);
    try {
      const res = await fetchWithTenant('/api/md/switches/start-monitoring', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ switchIds: [id] }) });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      showToast('Monitoring started');
      setTimeout(() => fetchSwitch(true), 3000);
    } catch (e) { showToast(`Failed: ${e.message}`, 'error'); }
    finally { setActionLoading(false); }
  };

  // Phase 1 (868m1ytt0): manual reclassify, sets isManual so rescans never
  // overwrite it (see the isManual guard in handleSwitchDiscovered).
  const handleReclassify = async (e) => {
    const category = e.target.value;
    if (!sw || category === (sw.category || 'UNKNOWN')) return;
    setActionLoading(true);
    try {
      const res = await fetchWithTenant(`/api/md/switches/${id}/category`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ category }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      showToast(`Reclassified as ${category}`);
      fetchSwitch(true);
    } catch (err) { showToast(`Reclassify failed: ${err.message}`, 'error'); }
    finally { setActionLoading(false); }
  };

  // Per-device credential override (868m2a981): fixes an integration whose
  // shared credential does not work for every device it covers. Saves
  // immediately on selection, same UX as handleReclassify above.
  const handleCredentialChange = async (credentialId) => {
    if (!sw || (credentialId || '') === (sw.credentialId || '')) return;
    setActionLoading(true);
    try {
      const res = await fetchWithTenant(`/api/md/switches/${id}/credential`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credentialId: credentialId || null }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      showToast(credentialId ? 'Credential override set' : 'Credential override cleared');
      fetchSwitch(true);
    } catch (err) { showToast(`Credential update failed: ${err.message}`, 'error'); }
    finally { setActionLoading(false); }
  };

  // Health/firmware ownership override (868m2z7an): AGENT/CONTROLLER/NONE,
  // same immediate-save UX as handleReclassify/handleCredentialChange above.
  // The backend rejects CONTROLLER with no linked controller (409), so the
  // option is disabled in the select below rather than relying on that.
  const handlePollModeChange = async (e) => {
    const pollMode = e.target.value;
    if (!sw || pollMode === (sw.pollMode || 'AGENT')) return;
    setActionLoading(true);
    try {
      const res = await fetchWithTenant(`/api/md/switches/${id}/poll-mode`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pollMode }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      showToast(`Health source set to ${pollMode}`);
      fetchSwitch(true);
    } catch (err) { showToast(`Poll mode update failed: ${err.message}`, 'error'); }
    finally { setActionLoading(false); }
  };

  const selectedPortData = selectedPort !== null
    ? ports.find(p => (p.id || p.portNumber) === selectedPort || p.label === selectedPort)
    : null;

  const totalPoe = ports.reduce((s, p) => s + (p.poeWatts || 0), 0);
  const portsUp = ports.filter(p => getPortStatus(p) === 'up').length;
  const portsErr = ports.filter(p => getPortStatus(p) === 'error').length;
  const poePct = sw?.poeBudgetWatts ? (totalPoe / sw.poeBudgetWatts) * 100 : 0;

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, gap: 10, color: 'var(--text-tertiary)', fontFamily: 'system-ui' }}>
      <Loader size={20} style={{ animation: 'spin 1s linear infinite' }} /> Loading switch…
      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
    </div>
  );
  if (error || !sw) return (
    <div style={{ padding: 24, fontFamily: 'system-ui' }}>
      <button onClick={() => navigate('/monitoring/network')} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, padding: 0, marginBottom: 16 }}>
        <ArrowLeft size={14} /> Back
      </button>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 16, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 8, color: '#ef4444', fontSize: 13 }}>
        <AlertCircle size={15} /> {error || 'Switch not found'}
      </div>
    </div>
  );

  // Routers get a dedicated view (WAN links, no ports/PoE) instead of the
  // switch layout. The router-specific telemetry lives in MdRouterDevice, not
  // the MdSwitchDevice row loaded here, so MdRouterDetail fetches it itself.
  if (sw.category === 'ROUTER') {
    return <MdRouterDetail sw={sw} navigate={navigate} />;
  }

  return (
    <div style={{ fontFamily: "'Segoe UI', system-ui, sans-serif", background: '#0C111B', color: 'var(--text-primary)', minHeight: '100vh', padding: '20px 24px' }}>

      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', top: 24, right: 24, zIndex: 9999, padding: '12px 18px', borderRadius: 8, background: toast.type === 'error' ? 'rgba(239,68,68,0.15)' : 'rgba(0,212,170,0.15)', border: `1px solid ${toast.type === 'error' ? 'rgba(239,68,68,0.3)' : 'rgba(0,212,170,0.3)'}`, color: toast.type === 'error' ? '#ef4444' : '#00D4AA', fontSize: 13, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 8 }}>
          {toast.type === 'error' ? <AlertCircle size={15} /> : <CheckCircle size={15} />}
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <DeviceHeader
        icon={CATEGORY_ICON[sw.category] || Network}
        online={sw.isOnline}
        title={sw.hostname || sw.managementIp}
        identityParts={[
          // A vendor with no MdManufacturer enum value is stored as
          // GENERIC_SNMP with its real name in manufacturerName; show that
          // name rather than the placeholder enum (868m1yttp).
          (sw.manufacturer === 'GENERIC_SNMP' && sw.manufacturerName) ? sw.manufacturerName : (sw.manufacturer || '—'),
          sw.model,
          sw.serial || sw.macAddress || '—',
          sw.managementIp,
        ]}
        secondaryLine={
          // Health/firmware ownership (868m2z7an): a CONTROLLER-mode row's
          // isOnline/firmware/etc. come from the linked controller, not this
          // device's own poll -- surface which one and how fresh.
          sw.pollMode === 'CONTROLLER' && sw.controller
            ? `Monitored via ${sw.controller.name}, last sync ${timeAgo(sw.controller.lastSyncAt)}`
            : null
        }
        backLabel="Network"
        onBack={() => navigate(-1)}
      >
          <ControlPill variant="interactive">
            <select
              className="dd-pill-select"
              value={sw.category || 'UNKNOWN'}
              onChange={handleReclassify}
              disabled={actionLoading}
              title="Reclassify device category"
            >
              <option value="SWITCH">Switch</option>
              <option value="ROUTER">Router</option>
              <option value="ACCESS_POINT">Access Point</option>
              <option value="FIREWALL">Firewall</option>
              <option value="UNKNOWN">Unclassified</option>
            </select>
          </ControlPill>
          <ControlPill variant="interactive">
            <CredentialPicker
              className="dd-pill-select"
              protocol="HTTP"
              value={sw.credentialId || ''}
              onChange={handleCredentialChange}
              disabled={actionLoading}
              title="Per-device credential override, falls back to the integration's credential when unset"
              defaultLabel="Use integration credential"
            />
          </ControlPill>
          <ControlPill variant="interactive">
            <select
              className="dd-pill-select"
              value={sw.pollMode || 'AGENT'}
              onChange={handlePollModeChange}
              disabled={actionLoading}
              title="Health/firmware source: this device's own agent poll, a linked controller, or neither"
            >
              <option value="AGENT">Health: Agent</option>
              <option value="CONTROLLER" disabled={!sw.controllerId}>Health: Controller</option>
              <option value="NONE">Health: None</option>
            </select>
          </ControlPill>
          <ControlPill variant="passive" icon={Cable} label="FW" value={sw.firmwareVersion || '—'} />
          <ControlPill variant="passive" icon={Clock} value={formatUptime(sw.uptime)} />
          {sw.pollMode === 'CONTROLLER' ? (
            <button onClick={handleForcePoll} disabled={actionLoading} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8, border: '1px solid #1E293B', background: '#131B2E', color: 'var(--text-secondary)', fontSize: 13, fontWeight: 500, cursor: actionLoading ? 'default' : 'pointer' }}>
              {actionLoading ? <Loader size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <RefreshCw size={13} />} Sync from controller
            </button>
          ) : sw.pollStatus === 'NEVER_SYNCED' ? (
            <button onClick={handleStartMonitoring} disabled={actionLoading} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8, border: 'none', background: 'rgba(0,212,170,0.15)', color: '#00D4AA', fontSize: 13, fontWeight: 500, cursor: actionLoading ? 'default' : 'pointer' }}>
              {actionLoading ? <Loader size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <Activity size={13} />} Start Monitoring
            </button>
          ) : (
            <button onClick={handleForcePoll} disabled={actionLoading} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8, border: '1px solid #1E293B', background: '#131B2E', color: 'var(--text-secondary)', fontSize: 13, fontWeight: 500, cursor: actionLoading ? 'default' : 'pointer' }}>
              {actionLoading ? <Loader size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <RefreshCw size={13} />} Force Poll
            </button>
          )}
      </DeviceHeader>

      {/* Quick Stats */}
      <div className="switch-quick-stats">
        {[
          { label: sw.category === 'ROUTER' ? 'Interfaces Up' : sw.category === 'ACCESS_POINT' ? 'Links Up' : 'Ports Up', value: `${portsUp}`, total: `${ports.length}`, color: '#10B981' },
          { label: 'Errors',          value: `${portsErr}`,                 total: null,              color: portsErr > 0 ? '#EF4444' : '#10B981' },
          { label: 'PoE Used',        value: `${totalPoe.toFixed(0)}W`,     total: sw.poeBudgetWatts ? `${sw.poeBudgetWatts}W` : null, color: poePct > 80 ? '#F59E0B' : '#06B6D4' },
          { label: 'PoE Budget',      value: sw.poeBudgetWatts ? `${poePct.toFixed(0)}%` : 'N/A', total: null, color: poePct > 90 ? '#EF4444' : poePct > 80 ? '#F59E0B' : '#10B981' },
          { label: 'Throughput', value: formatBps((switchRates.aggregate?.txBps || 0) + (switchRates.aggregate?.rxBps || 0)), total: null, color: '#3B82F6',
            spark: switchRates.rates?.length > 1 ? switchRates.rates.map(r => (r.txBps || 0) + (r.rxBps || 0)) : null },
        ].map((s, i) => (
          <div key={i} className="stat-widget">
            <div className="stat-widget-label">{s.label}</div>
            <div className="stat-widget-value" style={{ color: s.color }}>
              {s.value}{s.total && <span style={{ fontSize: 13, color: 'var(--text-tertiary)', fontWeight: 400 }}> / {s.total}</span>}
            </div>
            {s.spark && <Sparkline data={s.spark} color={s.color} width={120} height={22} />}
          </div>
        ))}
      </div>

      {/* Environment Monitoring (Cisco only — hidden when envSnapshot is null) */}
      {sw.envSnapshot && sw.envSnapshot.length > 0 && <EnvironmentCard envSnapshot={sw.envSnapshot} />}

      {/* Physical Panel — only for switches */}
      {portsLoading && ports.length === 0 ? (
        <DeviceCard style={{ padding: '40px 22px', marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, color: 'var(--text-tertiary)' }}>
          <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} /> Loading {sw.category === 'ROUTER' ? 'interface' : sw.category === 'ACCESS_POINT' ? 'radio' : 'port'} data…
        </DeviceCard>
      ) : ports.length > 0 && (sw.category === 'SWITCH' || !sw.category) ? (
        (() => {
          const chassisNumbers = [...new Set(ports.map(p => p.chassis).filter(c => c != null))].sort((a, b) => a - b);
          if (chassisNumbers.length > 1) {
            return chassisNumbers.map(ch => {
              const chassisPorts = ports.filter(p => p.chassis === ch);
              return <SwitchPanel key={ch} sw={sw} ports={chassisPorts} selectedPort={selectedPort} onSelectPort={setSelectedPort} chassisLabel={`Chassis ${ch}`} />;
            });
          }
          return <SwitchPanel sw={sw} ports={ports} selectedPort={selectedPort} onSelectPort={setSelectedPort} />;
        })()
      ) : null}

      {/* Port Detail Panel */}
      {selectedPortData && (
        <PortDetailPanel port={selectedPortData} sparkData={sparkData} onClose={() => setSelectedPort(null)} sw={sw} navigate={navigate} />
      )}

      {/* All ports: table on wide screens, cards on mobile (CSS picks one) */}
      {ports.length > 0 && <PortTable ports={ports} selectedPort={selectedPort} onSelectPort={setSelectedPort} sw={sw} />}
      {ports.length > 0 && <PortCards ports={ports} selectedPort={selectedPort} onSelectPort={setSelectedPort} sw={sw} />}

      {ports.length === 0 && !portsLoading && (
        <div className="scd-empty-state">
          <div style={{ fontSize: 32, opacity: 0.2 }}>🔌</div>
          {sw.pollMode === 'CONTROLLER' ? (
            // 868m3ru2j: a CONTROLLER-mode row has no periodic agent poll to
            // "wait for" at all, so the AGENT-mode copy below would be an
            // honest-sounding lie here -- worse, for TP-Link Omada
            // specifically, an idle port with nothing plugged in will
            // NEVER appear (confirmed live: Omada's Open API only reports
            // a port when it has a connected client, no link/speed/PoE
            // status exists at all under this vendor's API), so implying
            // more data is coming would be a permanent false promise, not
            // a momentary loading state.
            <span style={{ fontSize: 13 }}>
              {sw.manufacturer === 'TP_LINK'
                ? 'No connected devices reported by the Omada controller for this switch — TP-Link\'s Open API only reports a port when a device is plugged into it, and has no link/speed/PoE status at all. An idle port will never appear here.'
                : 'No port data available from the controller for this switch.'}
            </span>
          ) : (
            <span style={{ fontSize: 13 }}>No {sw.category === 'ROUTER' ? 'interface' : sw.category === 'ACCESS_POINT' ? 'radio' : 'port'} data yet — waiting for first successful poll</span>
          )}
        </div>
      )}

      {/* Radios (868m2z7an): CONTROLLER-sourced per-radio detail for an
          ACCESS_POINT row (band/channel/txPower/mode plus each BSSID/SSID
          it's broadcasting) -- empty/absent for an AGENT-mode AP, since the
          agent's own poll has no radio data to offer. Client count for the
          AP as a whole is already the Wireless Clients header below. */}
      {sw.category === 'ACCESS_POINT' && Array.isArray(sw.radios) && sw.radios.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#e5e7eb', marginBottom: 10 }}>
            Radios ({sw.radios.length})
          </div>
          <DeviceCard style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: 12 }}>
            {sw.radios.map((radio, i) => (
              <DeviceListRow key={i} style={{ padding: '8px 12px', fontSize: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ color: '#e5e7eb', fontWeight: 500 }}>{radio.band || 'Unknown band'}</div>
                  <div style={{ color: 'var(--text-tertiary)' }}>
                    {radio.channel != null ? `Ch ${radio.channel}` : ''}
                    {radio.txPower != null ? ` · ${radio.txPower} dBm` : ''}
                    {radio.mode ? ` · ${radio.mode}` : ''}
                  </div>
                </div>
                {Array.isArray(radio.bssids) && radio.bssids.length > 0 && (
                  <div style={{ marginTop: 4, color: 'var(--text-tertiary)' }}>
                    {radio.bssids.map((b, j) => (
                      <div key={j}>{b.ssid || 'Unknown SSID'} · {b.bssid}</div>
                    ))}
                  </div>
                )}
              </DeviceListRow>
            ))}
          </DeviceCard>
        </div>
      )}

      {/* Wireless Clients (868m26xrv): only for an ACCESS_POINT row, devices
          whose wirelessApMac matches this row's macAddress. */}
      {sw.category === 'ACCESS_POINT' && (
        <div style={{ marginTop: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#e5e7eb', marginBottom: 10 }}>
            Wireless Clients {wirelessClients.length > 0 && `(${wirelessClients.length})`}
          </div>
          {wirelessClients.length === 0 ? (
            <div className="scd-empty-state">
              <span style={{ fontSize: 13 }}>No wireless clients currently attributed to this access point</span>
            </div>
          ) : (
            <DeviceCard style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: 12 }}>
              {wirelessClients.map((client) => (
                <DeviceListRow key={client.id} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '8px 12px', fontSize: 12,
                }}>
                  <div>
                    <div style={{ color: '#e5e7eb', fontWeight: 500 }}>{client.hostname || client.name || client.ipAddress}</div>
                    <div style={{ color: 'var(--text-tertiary)', marginTop: 2 }}>{client.ipAddress} · {client.macAddress}</div>
                  </div>
                  <div style={{ textAlign: 'right', color: 'var(--text-tertiary)' }}>
                    <div>{client.wirelessSsid || 'Unknown SSID'}</div>
                    <div>{client.wirelessSignalDbm != null ? `${client.wirelessSignalDbm} dBm` : ''}</div>
                  </div>
                </DeviceListRow>
              ))}
            </DeviceCard>
          )}
        </div>
      )}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        button:focus { outline: none; }
        ::-webkit-scrollbar { height: 5px; width: 5px; }
        ::-webkit-scrollbar-track { background: #0C111B; }
        ::-webkit-scrollbar-thumb { background: #1E293B; border-radius: 3px; }
      `}</style>
    </div>
  );
}
